import React, { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { ArrowRightIcon, ShieldCheckIcon, DownloadIcon, EyeOffIcon, FileTextIcon, WrenchIcon, CheckIcon, LockIcon, ChevronDownIcon } from 'lucide-react';
export function PriceSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-100px'
  });
  const [bonusExpanded, setBonusExpanded] = useState(false);
  const [bump1Checked, setBump1Checked] = useState(false);
  const [bump2Checked, setBump2Checked] = useState(false);
  const [bump1Flash, setBump1Flash] = useState(false);
  const [bump2Flash, setBump2Flash] = useState(false);
  const handleBump1Toggle = () => {
    const next = !bump1Checked;
    setBump1Checked(next);
    if (next) {
      setBump1Flash(true);
      setTimeout(() => setBump1Flash(false), 2200);
    }
  };
  const handleBump2Toggle = () => {
    const next = !bump2Checked;
    setBump2Checked(next);
    if (next) {
      setBump2Flash(true);
      setTimeout(() => setBump2Flash(false), 2200);
    }
  };
  // Dynamic price calculation
  const totalPrice = (6.36 + (bump1Checked ? 9 : 0) + (bump2Checked ? 9 : 0)).toFixed(2);
  // Dynamic checkout URL based on selected order bumps
  const getCheckoutUrl = () => {
    if (bump1Checked && bump2Checked) {
      return 'https://www.betterbysteps.com/cart/44213480783965:1';
    } else if (bump1Checked) {
      return 'https://www.betterbysteps.com/cart/44213480620125:1';
    } else if (bump2Checked) {
      return 'https://www.betterbysteps.com/cart/44213480980573:1';
    } else {
      return 'https://www.betterbysteps.com/cart/44211527909469:1';
    }
  };
  // Flash animation for order bumps
  const bumpFlash = {
    animate: {
      boxShadow: ['0 0 0px rgba(224, 123, 57, 0)', '0 0 18px rgba(224, 123, 57, 0.45)', '0 0 0px rgba(224, 123, 57, 0)']
    },
    transition: {
      duration: 2.8,
      repeat: Infinity,
      repeatDelay: 1.5,
      ease: 'easeInOut'
    }
  };
  const bonusDetails = [{
    id: 1,
    title: 'The $500 Vault Materials Master List',
    value: '$47',
    image: 'https://cdn.magicpatterns.com/uploads/4DVj96KionDXbdG5Sjmk2q/bono_1_bunker_.png',
    problem: "The #1 reason people stall before they build? They don't know exactly what to buy — and end up with $200 in wrong materials and missing 3 critical items they actually need.",
    solution: 'This bonus eliminates that problem completely. Inside, you get:',
    bullets: ['The exact specs for every material you need (no brand guessing, no "tactical" markup)', 'A one-cart printable checklist — screenshot it, walk in, walk out', 'A substitution matrix so your build never stalls if a shelf is empty', 'A budget vs. upgrade breakdown — know exactly where to spend and where to save', 'A 10-minute cost calculator with 3 budget tiers ($250 / $350 / $500)'],
    bottomLine: "You'll know exactly what to buy, what to skip, and what to grab if something's out of stock. No wasted trips. No wasted money."
  }, {
    id: 2,
    title: 'The Invisible Placement Playbook',
    value: '$47',
    image: 'https://cdn.magicpatterns.com/uploads/7MvoVLSeywt95VxwLFJsHn/boono_2_bunker.png',
    problem: "You can build the most structurally sound vault in the world… and completely ruin it by putting it in the wrong spot. A hidden vault in a predictable location isn't hidden — it's just delayed.",
    solution: 'The Invisible Placement Playbook gives you the stealth logic:',
    bullets: ['The "Thief Search Pattern" — the exact sequence most burglars follow (so you can stay out of it)', 'Top dead-space zones in real American homes that get consistently overlooked', 'A "Do NOT Place Here" red-flag list — so you don\'t accidentally make your vault a target', 'A 10-minute Placement Scorecard — score your locations and pick the winner fast', 'The "Contractor Test" script — validate your spot without revealing your plan to anyone', '3 real placement examples with the reasoning behind each one'],
    bottomLine: 'You stop second-guessing yourself — and don\'t accidentally undo the entire point of "hidden" with a bad location choice.'
  }, {
    id: 3,
    title: 'Fire + Moisture Protection Protocol (Rust-Proof Kit)',
    value: '$47',
    image: 'https://cdn.magicpatterns.com/uploads/rEnvRa1TEhLPF82mCHneyu/bono_3_bunker_.png',
    problem: "Most gear doesn't get destroyed during a fire or flood. It gets destroyed after — by humidity, smoke residue, and corrosion that set in quietly while the damage goes unnoticed.",
    solution: 'A $20 setup and a 5-minute monthly habit can protect all of it. The Fire + Moisture Protection Protocol gives you:',
    bullets: ['The "3 Silent Killers" breakdown — humidity, condensation swings, and smoke residue explained simply', 'A 10-minute protection setup that works in any U.S. climate (hot, cold, humid, dry, coastal)', 'A simple desiccant placement map so you set it correctly the first time', 'A 5-minute monthly maintenance checklist (set a reminder, do the check, done)', "A 60-second quick check for when you're busy", 'After-Fire first-hour checklist — step-by-step to prevent secondary corrosion', 'After-Flood first-hour checklist — the #1 mistake is sealing it back up wet. This stops that.'],
    bottomLine: "Your vault doesn't just stay hidden. What's inside it survives time, climate, and real events — nationwide."
  }];
  // Dynamic order items based on selected bumps
  const orderItems = [{
    label: 'The Concrete Bunker Playbook (Main Guide)',
    value: '$97',
    isFree: false,
    isBump: false
  }, {
    label: 'BONUS #1 — The $500 Vault Materials Master List',
    value: '$47',
    isFree: true,
    isBump: false
  }, {
    label: 'BONUS #2 — The Invisible Placement Playbook',
    value: '$47',
    isFree: true,
    isBump: false
  }, {
    label: 'BONUS #3 — Fire + Moisture Protection Protocol',
    value: '$47',
    isFree: true,
    isBump: false
  }, ...(bump1Checked ? [{
    label: 'ADD-ON — The Contractor Smoke Screen Script',
    value: '$47',
    isFree: false,
    isBump: true,
    bumpPrice: '$9.00'
  }] : []), ...(bump2Checked ? [{
    label: 'ADD-ON — The Invisible Inventory Legacy Protocol',
    value: '$47',
    isFree: false,
    isBump: true,
    bumpPrice: '$9.00'
  }] : [])];
  const trustBadges = [{
    icon: DownloadIcon,
    label: 'Instant Download'
  }, {
    icon: EyeOffIcon,
    label: 'No Paper Trail'
  }, {
    icon: LockIcon,
    label: 'Secure Checkout'
  }, {
    icon: ShieldCheckIcon,
    label: '60-Day Guarantee'
  }];
  return <section id="checkout" ref={ref} className="w-full bg-dark-section py-20 md:py-28" data-id="element-183">
      <div className="max-w-2xl mx-auto px-6" data-id="element-184">
        {/* Headline */}
        <motion.div className="text-center mb-10" initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 20
      }} transition={{
        duration: 0.6
      }} data-id="element-185">
          <span className="inline-block px-3 py-1 text-xs font-inter font-bold tracking-[0.2em] text-orange-accent uppercase border border-orange-accent/50 bg-orange-accent/10 mb-4" data-id="element-186">
            COMPLETE YOUR ORDER
          </span>
          <h2 className="font-bebas text-4xl md:text-5xl text-white tracking-wide" data-id="element-187">
            STOP BEING A TARGET.{' '}
            <span className="text-orange-accent" data-id="element-188">START BEING A GHOST.</span>
          </h2>
        </motion.div>

        {/* Order card */}
        <motion.div className="bg-dark-card border border-white/10 overflow-hidden" initial={{
        opacity: 0,
        y: 24
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 24
      }} transition={{
        duration: 0.6,
        delay: 0.15
      }} data-id="element-189">
          {/* Card header */}
          <div className="px-6 py-4 bg-dark-bg border-b border-white/5" data-id="element-190">
            <p className="font-inter text-xs font-semibold tracking-[0.2em] text-concrete/60 uppercase" data-id="element-191">
              Order Summary
            </p>
          </div>

          {/* Line items */}
          <div className="divide-y divide-white/5" data-id="element-192">
            <AnimatePresence initial={false} data-id="element-193">
              {orderItems.map((item, index) => <motion.div key={item.label} className="flex items-center justify-between px-6 py-4 gap-4" initial={{
              opacity: 0,
              x: item.isBump ? 0 : -10,
              height: item.isBump ? 0 : 'auto',
              y: item.isBump ? -6 : 0
            }} animate={{
              opacity: 1,
              x: 0,
              height: 'auto',
              y: 0
            }} exit={{
              opacity: 0,
              height: 0,
              y: -6
            }} transition={{
              duration: item.isBump ? 0.35 : 0.4,
              delay: item.isBump ? 0 : 0.25 + index * 0.08,
              ease: [0.25, 0.46, 0.45, 0.94]
            }} style={{
              overflow: 'hidden'
            }} data-id="element-194">
                  <div className="flex items-center gap-3" data-id="element-195">
                    <div className={`w-7 h-7 flex-shrink-0 flex items-center justify-center border ${item.isBump ? 'bg-orange-accent/20 border-orange-accent/60' : 'bg-orange-accent/10 border-orange-accent/30'}`} data-id="element-196">
                      <CheckIcon className={`w-3.5 h-3.5 ${item.isBump ? 'text-orange-accent' : 'text-orange-accent'}`} data-id="element-197" />
                    </div>
                    <span className={`font-inter text-sm leading-snug ${item.isBump ? 'text-orange-accent/90 font-semibold' : 'text-white'}`} data-id="element-198">
                      {item.label}
                    </span>
                  </div>
                  <div className="flex flex-col items-end flex-shrink-0 gap-0.5" data-id="element-199">
                    <span className="font-inter text-sm text-concrete/50 line-through" data-id="element-200">
                      {item.value}
                    </span>
                    {item.isFree && <span className="font-inter text-xs font-bold text-orange-accent uppercase tracking-wide" data-id="element-201">
                        FREE
                      </span>}
                    {item.isBump && <span className="font-inter text-xs font-bold text-orange-accent uppercase tracking-wide" data-id="element-202">
                        {item.bumpPrice}
                      </span>}
                  </div>
                </motion.div>)}
            </AnimatePresence>
          </div>

          {/* Read more toggle */}
          <div className="border-t border-white/5" data-id="element-203">
            <button onClick={() => setBonusExpanded(!bonusExpanded)} className="w-full flex items-center justify-center gap-2 px-6 py-3 text-concrete/60 hover:text-orange-accent transition-colors duration-200" data-id="element-204">
              <span className="font-inter text-xs font-semibold tracking-[0.15em] uppercase" data-id="element-205">
                {bonusExpanded ? 'Show less' : "See what's inside each bonus"}
              </span>
              <motion.div animate={{
              rotate: bonusExpanded ? 180 : 0
            }} transition={{
              duration: 0.3
            }} data-id="element-206">
                <ChevronDownIcon className="w-4 h-4" data-id="element-207" />
              </motion.div>
            </button>

            <AnimatePresence data-id="element-208">
              {bonusExpanded && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.4,
              ease: [0.25, 0.46, 0.45, 0.94]
            }} className="overflow-hidden" data-id="element-209">
                  <div className="px-6 pb-6 space-y-6 border-t border-white/5 pt-6" data-id="element-210">
                    {bonusDetails.map(bonus => <div key={bonus.id} className="flex flex-col sm:flex-row gap-4" data-id="element-211">
                        <img src={bonus.image} alt={bonus.title} className="w-full sm:w-28 sm:h-36 object-cover flex-shrink-0 shadow-lg" data-id="element-212" />
                        <div className="flex-1" data-id="element-213">
                          <div className="flex items-baseline gap-2 mb-1" data-id="element-214">
                            <span className="font-bebas text-orange-accent text-base tracking-wide" data-id="element-215">
                              BONUS #{bonus.id}
                            </span>
                            <span className="font-inter text-concrete/50 text-xs line-through" data-id="element-216">
                              {bonus.value}
                            </span>
                            <span className="font-inter text-xs font-bold text-orange-accent" data-id="element-217">
                              FREE
                            </span>
                          </div>
                          <h4 className="font-bebas text-xl text-white mb-2 tracking-wide leading-none" data-id="element-218">
                            {bonus.title}
                          </h4>
                          <p className="font-inter text-xs text-concrete mb-3 leading-relaxed" data-id="element-219">
                            <strong className="text-white/80" data-id="element-220">
                              {bonus.problem}
                            </strong>
                          </p>
                          <ul className="space-y-1.5 mb-3" data-id="element-221">
                            {bonus.bullets.map((bullet, i) => <li key={i} className="flex items-start gap-2" data-id="element-222">
                                <CheckIcon className="w-3 h-3 text-orange-accent flex-shrink-0 mt-0.5" data-id="element-223" />
                                <span className="font-inter text-xs text-concrete/80 leading-relaxed" data-id="element-224">
                                  {bullet}
                                </span>
                              </li>)}
                          </ul>
                          <p className="font-inter text-xs text-concrete/70 italic border-t border-white/5 pt-2" data-id="element-225">
                            <strong className="text-white/70 not-italic" data-id="element-226">
                              Bottom line:
                            </strong>{' '}
                            {bonus.bottomLine}
                          </p>
                        </div>
                      </div>)}
                  </div>
                </motion.div>}
            </AnimatePresence>
          </div>

          {/* Totals */}
          <motion.div className="border-t border-white/10 px-6 py-5 bg-dark-bg/50" initial={{
          opacity: 0
        }} animate={isInView ? {
          opacity: 1
        } : {
          opacity: 0
        }} transition={{
          duration: 0.5,
          delay: 0.6
        }} data-id="element-227">
            <div className="flex items-center justify-between mb-3" data-id="element-228">
              <span className="font-inter text-sm text-concrete" data-id="element-229">
                Total Value
              </span>
              <span className="font-inter text-sm text-concrete/50 line-through" data-id="element-230">
                $238
              </span>
            </div>
            <div className="flex items-center justify-between" data-id="element-231">
              <span className="font-inter text-base font-semibold text-white" data-id="element-232">
                Your Price Today
              </span>
              <motion.span key={totalPrice} className="font-bebas text-4xl text-orange-accent tracking-wider" initial={{
              opacity: 0,
              scale: 0.85
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.3,
              type: 'spring',
              stiffness: 260,
              damping: 18
            }} data-id="element-233">
                ${totalPrice}
              </motion.span>
            </div>
            <p className="font-inter text-xs text-concrete/50 mt-2" data-id="element-234">
              One-time payment · No subscription · No recurring fees
            </p>
          </motion.div>

          {/* CTA */}
          <motion.div className="px-6 pb-6 pt-4" initial={{
          opacity: 0,
          y: 10
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {
          opacity: 0,
          y: 10
        }} transition={{
          duration: 0.5,
          delay: 0.85
        }} data-id="element-235">
            {/* Top CTA button */}
            <motion.button onClick={() => {
            if (window.fbq) window.fbq('track', 'AddToCart', {
              value: parseFloat(totalPrice),
              currency: 'USD'
            });
            window.open(getCheckoutUrl(), '_blank');
          }} className="w-full flex items-center justify-center px-6 py-5 bg-orange-accent text-white font-inter font-bold text-base uppercase tracking-wide rounded-sm transition-all duration-300 hover:bg-orange-accent/90 hover:scale-[1.01] active:scale-[0.99]" whileHover={{
            boxShadow: '0 0 40px rgba(224, 123, 57, 0.45)'
          }} data-id="element-236">
              GET INSTANT ACCESS — ${totalPrice}
            </motion.button>

            {/* ORDER BUMP #1 */}
            <motion.div className="mt-4 border-2 border-orange-accent bg-[#1a1208] rounded-sm overflow-hidden" initial={{
            opacity: 0,
            y: 8
          }} animate={isInView ? {
            opacity: 1,
            y: 0,
            boxShadow: bumpFlash.animate.boxShadow
          } : {
            opacity: 0,
            y: 8
          }} transition={isInView ? {
            opacity: {
              duration: 0.5,
              delay: 0.95
            },
            y: {
              duration: 0.5,
              delay: 0.95
            },
            boxShadow: {
              ...bumpFlash.transition,
              delay: 2
            }
          } : {
            duration: 0.5,
            delay: 0.95
          }} data-id="element-237">
              {/* Bump header stripe */}
              <div className="px-5 py-3 bg-orange-accent/10 border-b-2 border-orange-accent/40" data-id="element-238">
                <div className="flex items-center justify-between gap-3 mb-2" data-id="element-239">
                  <span className="font-inter font-black text-[11px] tracking-[0.2em] text-orange-accent uppercase leading-none" data-id="element-240">
                    ⭐ Highly Recommended Add-On
                  </span>
                  <motion.button onClick={handleBump1Toggle} className={`font-inter font-black text-xs text-white px-3 py-1.5 flex-shrink-0 flex items-center gap-1.5 transition-all duration-300 cursor-pointer ${bump1Checked ? 'bg-green-600 hover:bg-green-700' : 'bg-orange-accent hover:bg-orange-accent/80'}`} whileTap={{
                  scale: 0.95
                }} whileHover={{
                  scale: 1.04
                }} title={bump1Checked ? 'Click to remove from order' : 'Click to add to order'} data-id="element-241">
                    <AnimatePresence mode="wait" data-id="element-242">
                      {bump1Checked ? <motion.span key="added" className="flex items-center gap-1" initial={{
                      opacity: 0,
                      y: -6
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} exit={{
                      opacity: 0,
                      y: 6
                    }} transition={{
                      duration: 0.2
                    }} data-id="element-243">
                          <CheckIcon className="w-3 h-3" data-id="element-244" />
                          Added!
                        </motion.span> : <motion.span key="add" initial={{
                      opacity: 0,
                      y: -6
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} exit={{
                      opacity: 0,
                      y: 6
                    }} transition={{
                      duration: 0.2
                    }} data-id="element-245">
                          + $9.00
                        </motion.span>}
                    </AnimatePresence>
                  </motion.button>
                </div>
                {/* Success flash message */}
                <AnimatePresence data-id="element-246">
                  {bump1Flash && <motion.p className="font-inter text-[11px] font-semibold text-green-400 mb-1" initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.25
                }} data-id="element-247">
                      ✓ Successfully added to your order!
                    </motion.p>}
                </AnimatePresence>
                <p className="font-bebas text-xl md:text-2xl text-white tracking-wide leading-tight" data-id="element-248">
                  WAIT! DON'T GET FLAGGED BY YOUR NEIGHBORS
                </p>
              </div>

              {/* Product image — full width, prominent */}
              <div className="w-full" data-id="element-249">
                <img src="https://cdn.magicpatterns.com/uploads/oeeAa3sxNessRPMe4JcPA9/order_bump_1_bunker_.png" alt="The Contractor Smoke Screen Script" className="w-full h-48 sm:h-56 object-cover" data-id="element-250" />
              </div>

              {/* Copy */}
              <div className="px-5 py-4 text-left" data-id="element-251">
                <p className="font-bebas text-2xl text-white tracking-wide leading-tight mb-0.5" data-id="element-252">
                  The Privacy Shield
                </p>
                <p className="font-inter font-bold text-xs text-orange-accent uppercase tracking-[0.15em] mb-3" data-id="element-253">
                  Add "The Contractor Smoke Screen Script" to your order for
                  just $9.00!
                </p>
                <p className="font-inter text-sm text-concrete/80 leading-relaxed mb-2" data-id="element-254">
                  Building a vault is 100% legal — but curiosity is dangerous.
                  When neighbors or hardware store employees ask why you're
                  buying high-strength concrete and steel, you need a story that
                  shuts it down fast.
                </p>
                <p className="font-inter text-sm text-concrete/80 leading-relaxed" data-id="element-255">
                  Inside: exact "Boring Home Repair" scripts for every purchase
                  and every loud noise — from "Foundation Stabilization" to
                  "Storm Shelter" cover stories. You stay invisible while you
                  build.
                </p>

                {/* Pricing row */}
                <div className="flex items-center gap-3 mt-4 mb-4" data-id="element-256">
                  <span className="font-inter text-sm text-concrete/40 line-through" data-id="element-257">
                    $47
                  </span>
                  <span className="font-bebas text-2xl text-orange-accent tracking-wider" data-id="element-258">
                    $9.00
                  </span>
                  <span className="font-inter text-xs text-concrete/50 bg-white/5 border border-white/10 px-2 py-0.5" data-id="element-259">
                    Save 80%
                  </span>
                </div>

                {/* Checkbox CTA */}
                <button onClick={handleBump1Toggle} className="w-full flex items-start gap-3 px-4 py-3 border-2 border-orange-accent/50 bg-orange-accent/5 hover:bg-orange-accent/10 hover:border-orange-accent transition-all duration-200 cursor-pointer text-left" data-id="element-260">
                  <div className={`flex-shrink-0 w-5 h-5 border-2 flex items-center justify-center transition-all duration-200 mt-0.5 ${bump1Checked ? 'bg-orange-accent border-orange-accent' : 'border-white/50 bg-transparent'}`} data-id="element-261">
                    {bump1Checked && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3.5} data-id="element-262">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-263" />
                      </svg>}
                  </div>
                  <span className="font-inter text-sm font-bold text-white leading-snug" data-id="element-264">
                    YES! I want the "Contractor Smoke Screen" Script for only{' '}
                    <span className="text-orange-accent" data-id="element-265">$9.00!</span>{' '}
                    <span className="font-normal text-concrete/50" data-id="element-266">
                      (Save 80% off the regular price)
                    </span>
                    <span className="block font-normal text-concrete/40 text-xs mt-1" data-id="element-267">
                      One-time offer · Won't be shown again
                    </span>
                  </span>
                </button>
              </div>
            </motion.div>

            {/* ORDER BUMP #2 */}
            <motion.div className="mt-4 border-2 border-orange-accent bg-[#1a1208] rounded-sm overflow-hidden" initial={{
            opacity: 0,
            y: 8
          }} animate={isInView ? {
            opacity: 1,
            y: 0,
            boxShadow: bumpFlash.animate.boxShadow
          } : {
            opacity: 0,
            y: 8
          }} transition={isInView ? {
            opacity: {
              duration: 0.5,
              delay: 1.05
            },
            y: {
              duration: 0.5,
              delay: 1.05
            },
            boxShadow: {
              ...bumpFlash.transition,
              delay: 3.2
            }
          } : {
            duration: 0.5,
            delay: 1.05
          }} data-id="element-268">
              {/* Bump header stripe */}
              <div className="px-5 py-3 bg-orange-accent/10 border-b-2 border-orange-accent/40" data-id="element-269">
                <div className="flex items-center justify-between gap-3 mb-2" data-id="element-270">
                  <span className="font-inter font-black text-[11px] tracking-[0.2em] text-orange-accent uppercase leading-none" data-id="element-271">
                    ⭐ Highly Recommended Add-On
                  </span>
                  <motion.button onClick={handleBump2Toggle} className={`font-inter font-black text-xs text-white px-3 py-1.5 flex-shrink-0 flex items-center gap-1.5 transition-all duration-300 cursor-pointer ${bump2Checked ? 'bg-green-600 hover:bg-green-700' : 'bg-orange-accent hover:bg-orange-accent/80'}`} whileTap={{
                  scale: 0.95
                }} whileHover={{
                  scale: 1.04
                }} title={bump2Checked ? 'Click to remove from order' : 'Click to add to order'} data-id="element-272">
                    <AnimatePresence mode="wait" data-id="element-273">
                      {bump2Checked ? <motion.span key="added" className="flex items-center gap-1" initial={{
                      opacity: 0,
                      y: -6
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} exit={{
                      opacity: 0,
                      y: 6
                    }} transition={{
                      duration: 0.2
                    }} data-id="element-274">
                          <CheckIcon className="w-3 h-3" data-id="element-275" />
                          Added!
                        </motion.span> : <motion.span key="add" initial={{
                      opacity: 0,
                      y: -6
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} exit={{
                      opacity: 0,
                      y: 6
                    }} transition={{
                      duration: 0.2
                    }} data-id="element-276">
                          + $9.00
                        </motion.span>}
                    </AnimatePresence>
                  </motion.button>
                </div>
                {/* Success flash message */}
                <AnimatePresence data-id="element-277">
                  {bump2Flash && <motion.p className="font-inter text-[11px] font-semibold text-green-400 mb-1" initial={{
                  opacity: 0,
                  height: 0
                }} animate={{
                  opacity: 1,
                  height: 'auto'
                }} exit={{
                  opacity: 0,
                  height: 0
                }} transition={{
                  duration: 0.25
                }} data-id="element-278">
                      ✓ Successfully added to your order!
                    </motion.p>}
                </AnimatePresence>
                <p className="font-bebas text-xl md:text-2xl text-white tracking-wide leading-tight" data-id="element-279">
                  SECURE YOUR FAMILY'S FUTURE (WITHOUT A MAP)
                </p>
              </div>

              {/* Product image — full width, prominent */}
              <div className="w-full" data-id="element-280">
                <img src="https://cdn.magicpatterns.com/uploads/hkBcg6ibgnUdRLJjRNKzxP/order_bump_2_bunker_.png" alt="The Invisible Inventory Legacy Protocol" className="w-full h-48 sm:h-56 object-cover" data-id="element-281" />
              </div>

              {/* Copy */}
              <div className="px-5 py-4 text-left" data-id="element-282">
                <p className="font-bebas text-2xl text-white tracking-wide leading-tight mb-0.5" data-id="element-283">
                  The Legacy Protocol
                </p>
                <p className="font-inter font-bold text-xs text-orange-accent uppercase tracking-[0.15em] mb-3" data-id="element-284">
                  Add "The Invisible Inventory Legacy Protocol" for just $9.00!
                </p>
                <p className="font-inter text-sm text-concrete/80 leading-relaxed mb-2" data-id="element-285">
                  If something happens to you tomorrow, does your family know
                  how to access your vault — without leaving a trail a thief
                  could follow?
                </p>
                <p className="font-inter text-sm text-concrete/80 leading-relaxed" data-id="element-286">
                  This protocol teaches the "Shadow Catalog" method: document
                  thousands in assets using a "tool-code" system that looks like
                  a boring garage inventory to anyone else. Your family gets the
                  keys. No one else does.
                </p>

                {/* Pricing row */}
                <div className="flex items-center gap-3 mt-4 mb-4" data-id="element-287">
                  <span className="font-inter text-sm text-concrete/40 line-through" data-id="element-288">
                    $47
                  </span>
                  <span className="font-bebas text-2xl text-orange-accent tracking-wider" data-id="element-289">
                    $9.00
                  </span>
                  <span className="font-inter text-xs text-concrete/50 bg-white/5 border border-white/10 px-2 py-0.5" data-id="element-290">
                    Save 80%
                  </span>
                </div>

                {/* Checkbox CTA */}
                <button onClick={handleBump2Toggle} className="w-full flex items-start gap-3 px-4 py-3 border-2 border-orange-accent/50 bg-orange-accent/5 hover:bg-orange-accent/10 hover:border-orange-accent transition-all duration-200 cursor-pointer text-left" data-id="element-291">
                  <div className={`flex-shrink-0 w-5 h-5 border-2 flex items-center justify-center transition-all duration-200 mt-0.5 ${bump2Checked ? 'bg-orange-accent border-orange-accent' : 'border-white/50 bg-transparent'}`} data-id="element-292">
                    {bump2Checked && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3.5} data-id="element-293">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" data-id="element-294" />
                      </svg>}
                  </div>
                  <span className="font-inter text-sm font-bold text-white leading-snug" data-id="element-295">
                    YES! I want the "Invisible Inventory" Legacy Protocol for
                    only <span className="text-orange-accent" data-id="element-296">$9.00!</span>{' '}
                    <span className="font-normal text-concrete/50" data-id="element-297">
                      (One-time offer)
                    </span>
                    <span className="block font-normal text-concrete/40 text-xs mt-1" data-id="element-298">
                      One-time offer · Won't be shown again
                    </span>
                  </span>
                </button>
              </div>
            </motion.div>

            {/* Bottom CTA button */}
            <motion.div className="mt-6" initial={{
            opacity: 0,
            y: 10
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {
            opacity: 0,
            y: 10
          }} transition={{
            duration: 0.5,
            delay: 1.2
          }} data-id="element-299">
              <motion.button onClick={() => {
              if (window.fbq) window.fbq('track', 'AddToCart', {
                value: parseFloat(totalPrice),
                currency: 'USD'
              });
              window.open(getCheckoutUrl(), '_blank');
            }} className="w-full flex items-center justify-center gap-2 px-6 py-5 bg-orange-accent text-white font-inter font-bold text-base uppercase tracking-wide rounded-sm transition-all duration-300 hover:bg-orange-accent/90 hover:scale-[1.01] active:scale-[0.99]" whileHover={{
              boxShadow: '0 0 40px rgba(224, 123, 57, 0.45)'
            }} data-id="element-300">
                <LockIcon className="w-4 h-4" data-id="element-301" />
                COMPLETE MY ORDER — ${totalPrice}
              </motion.button>
              <p className="font-inter text-xs text-concrete/40 text-center mt-2" data-id="element-302">
                Secure checkout · Instant access · 60-day guarantee
              </p>
            </motion.div>
          </motion.div>
        </motion.div>

        {/* Trust badges */}
        <motion.div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6" initial={{
        opacity: 0,
        y: 10
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {
        opacity: 0,
        y: 10
      }} transition={{
        duration: 0.5,
        delay: 1
      }} data-id="element-303">
          {trustBadges.map(badge => <div key={badge.label} className="flex flex-col items-center gap-2 py-3 px-2 bg-dark-card border border-white/5" data-id="element-304">
              <badge.icon className="w-5 h-5 text-orange-accent" data-id="element-305" />
              <span className="font-inter text-xs text-concrete/60 text-center leading-tight" data-id="element-306">
                {badge.label}
              </span>
            </div>)}
        </motion.div>
      </div>
    </section>;
}